import React, { useMemo, useState, createContext, ReactNode, useCallback } from 'react';
import { ThemeProvider, CssBaseline, createTheme } from '@mui/material';
import { getAppTheme, getDefaultThemeConfig } from 'shared/utils/theme';
import { CompanyThemeSettings, ThemePalette } from 'shared/types';

// Define the shape of the context values
interface ColorModeContextType {
  toggleColorMode: () => void;
  mode: 'light' | 'dark';
  companyTheme: CompanyThemeSettings | null;
  setCompanyTheme: (theme: CompanyThemeSettings | null) => void;
  logoUrl: string | null;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}

// Create the Context
export const ColorModeContext = createContext<ColorModeContextType>({
  toggleColorMode: () => {},
  mode: 'light',
  companyTheme: null,
  setCompanyTheme: () => {},
  logoUrl: null,
  isLoading: false,
  setIsLoading: () => {},
});

interface ThemeWrapperProps {
  children: ReactNode;
}

// Helper to get initial mode from local storage
const getInitialMode = (): 'light' | 'dark' => {
  const storedMode = localStorage.getItem('theme-mode') as 'light' | 'dark' | null;
  return storedMode || 'light';
};

// Helper to merge company theme with default theme
const mergeThemeWithDefaults = (
  mode: 'light' | 'dark',
  companyTheme: CompanyThemeSettings | null
) => {
  const defaultConfig = getDefaultThemeConfig(mode);

  if (!companyTheme) {
    return createTheme(defaultConfig);
  }

  // Get the flat palette for the current mode
  const themePalette: ThemePalette | undefined = mode === 'light'
    ? companyTheme.lightTheme
    : companyTheme.darkTheme;

  if (!themePalette) {
    return createTheme(defaultConfig);
  }

  // Merge company theme palette with defaults
  const defaultBackground = defaultConfig.palette.background as Record<string, string>;
  // Treat legacy white as "no preference" so the new default applies
  const isLegacyWhite = (v?: string) => !v || v.toUpperCase() === '#FFFFFF' || v.toUpperCase() === '#FFF';
  const mergedBackground = {
    ...defaultBackground,
    default: isLegacyWhite(themePalette.default) ? defaultBackground.default : themePalette.default,
    paper: themePalette.paper || defaultBackground.paper,
    subtle: defaultBackground.subtle,
    primary: themePalette.primary || defaultBackground.primary,
    sidebar: themePalette.sidebar || defaultBackground.sidebar,
    sidebartext: themePalette.sidebartext || defaultBackground.sidebartext,
    sidebarSelectedBg: isLegacyWhite(themePalette.sidebarSelectedBg) ? defaultBackground.sidebarSelectedBg : themePalette.sidebarSelectedBg,
    sidebarSelectedText: themePalette.sidebarSelectedText || defaultBackground.sidebarSelectedText,
    buttonBg: themePalette.buttonBg || defaultBackground.buttonBg,
    buttonText: themePalette.buttonText || defaultBackground.buttonText,
    chipSuccess: themePalette.chipSuccess || defaultBackground.chipSuccess,
    chipWarning: themePalette.chipWarning || defaultBackground.chipWarning,
    chipError: themePalette.chipError || defaultBackground.chipError,
    chipInfo: themePalette.chipInfo || defaultBackground.chipInfo,
    tableRowHoverDefault: themePalette.tableRowHoverDefault || defaultBackground.tableRowHoverDefault,
    tableRowHoverError: themePalette.tableRowHoverError || defaultBackground.tableRowHoverError,
    tableRowHoverWarning: themePalette.tableRowHoverWarning || defaultBackground.tableRowHoverWarning,
  };

  return createTheme({
    palette: {
      mode,
      primary: { main: themePalette.primary || (defaultConfig.palette.primary as any).main },
      secondary: defaultConfig.palette.secondary,
      success: defaultConfig.palette.success,
      error: defaultConfig.palette.error,
      warning: defaultConfig.palette.warning,
      info: defaultConfig.palette.info,
      domain: defaultConfig.palette.domain,
      taskType: defaultConfig.palette.taskType,
      brand: defaultConfig.palette.brand,
      background: mergedBackground,
    },
    // Carry over the shared component overrides (small-screen button/tab
    // polish) so company-themed tenants get them too — this createTheme call
    // builds a fresh palette and would otherwise drop them.
    components: defaultConfig.components,
  });
};

export function ThemeWrapper({ children }: ThemeWrapperProps) {
  const [mode, setMode] = useState<'light' | 'dark'>(getInitialMode);
  const [companyTheme, setCompanyTheme] = useState<CompanyThemeSettings | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const toggleColorMode = useCallback(() => {
    setMode((prevMode) => {
      const newMode = prevMode === 'light' ? 'dark' : 'light';
      localStorage.setItem('theme-mode', newMode);
      return newMode;
    });
  }, []);

  const colorMode = useMemo(
    () => ({
      mode,
      toggleColorMode,
      companyTheme,
      setCompanyTheme,
      logoUrl: companyTheme?.logoUrl || null,
      isLoading,
      setIsLoading,
    }),
    [mode, toggleColorMode, companyTheme, isLoading],
  );

  // Create theme based on mode and company theme
  const theme = useMemo(() => {
    if (companyTheme) {
      return mergeThemeWithDefaults(mode, companyTheme);
    }
    return getAppTheme(mode);
  }, [mode, companyTheme]);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}
